import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { TestQuestion } from '../../services/api/test';

interface LocationState {
  answers: { [key: string]: string };
  questions: TestQuestion[];
  usedTime: number;
}

const TestResultPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  
  // 添加安全检查，确保state存在且结构正确
  const state = location.state as LocationState | null;
  
  // 处理state不存在或结构不正确的情况
  if (!state || !state.questions || !Array.isArray(state.questions) || !state.answers || typeof state.usedTime !== 'number') {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="text-center max-w-md px-4">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Test results unavailable</h2>
          <p className="text-gray-600 mb-6">Please complete the test first before viewing results</p>
          <button
            onClick={() => navigate('/dashboard')}
            className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  const { answers, questions, usedTime } = state;

  // 确保answers是一个对象
  const safeAnswers = typeof answers === 'object' && answers !== null ? answers : {};

  // 计算分数 - 添加安全检查
  const calculateScore = () => {
    if (!questions || questions.length === 0) {
      return { totalScore: 0, pinyinScore: 0, charScore: 0, correctCount: 0, totalCount: 0 };
    }

    let correctCount = 0;
    let pinyinQuestions = [];
    let charQuestions = [];
    let correctPinyinCount = 0;
    let correctCharCount = 0;

    try {
      pinyinQuestions = questions.filter(q => q.questionType === 'pinyin-recognition');
      charQuestions = questions.filter(q => q.questionType === 'character-recognition');

      // 计算正确答案数量
      questions.forEach(q => {
        const userAnswer = safeAnswers[q.questionId];
        if (userAnswer === q.correctAnswer) {
          correctCount++;
          if (q.questionType === 'pinyin-recognition') {
            correctPinyinCount++;
          } else {
            correctCharCount++;
          }
        }
      });
    } catch (error) {
      console.error('Error calculating score:', error);
    }

    const totalCount = questions.length;
    const totalScore = totalCount > 0 ? Math.round((correctCount / totalCount) * 100) : 0;
    const pinyinScore = pinyinQuestions.length > 0 ? Math.round((correctPinyinCount / pinyinQuestions.length) * 100) : 0;
    const charScore = charQuestions.length > 0 ? Math.round((correctCharCount / charQuestions.length) * 100) : 0;

    return { totalScore, pinyinScore, charScore, correctCount, totalCount };
  };

  const { totalScore, pinyinScore, charScore, correctCount, totalCount } = calculateScore();

  // Determine HSK level
  const determineLevel = (score: number) => {
    if (score >= 90) return { level: 'HSK 1+', description: 'Excellent' };
    if (score >= 80) return { level: 'HSK 1', description: 'Good' };
    if (score >= 60) return { level: 'HSK 1-', description: 'Pass' };
    return { level: 'Beginner Prep', description: 'Needs further study' };
  };

  const { level, description } = determineLevel(totalScore);

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center">
              <h1 className="text-xl font-bold text-gray-900">HSK Level Test Results</h1>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="px-4 py-2 text-sm font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
              >
                Back to Home
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Test completed!</h2>
            <p className="text-lg text-gray-600">
              You completed {totalCount} questions in {Math.floor(usedTime / 60)}:{String(usedTime % 60).padStart(2, '0')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            <div className="bg-primary/10 p-6 rounded-lg text-center">
              <h3 className="text-sm font-medium text-primary mb-2">Total Score</h3>
              <p className="text-4xl font-bold text-primary">{totalScore}%</p>
              <p className="text-sm text-primary mt-2">{correctCount}/{totalCount} correct</p>
            </div>

            <div className="bg-secondary/10 p-6 rounded-lg text-center">
              <h3 className="text-sm font-medium text-secondary mb-2">Pinyin Score</h3>
              <p className="text-4xl font-bold text-secondary">{pinyinScore}%</p>
            </div>

            <div className="bg-purple-100 p-6 rounded-lg text-center">
              <h3 className="text-sm font-medium text-purple-600 mb-2">Character Score</h3>
              <p className="text-4xl font-bold text-purple-700">{charScore}%</p>
            </div>
          </div>

          <div className="bg-primary/5 p-6 rounded-lg mb-8 text-center">
            <h3 className="text-lg font-semibold text-primary mb-2">Your HSK Level</h3>
            <p className="text-3xl font-bold text-primary">{level}</p>
            <p className="text-sm text-primary mt-2">{description}</p>
          </div>

          <div className="mb-8">
            <h3 className="text-xl font-semibold text-gray-900 mb-4">Question Explanations</h3>
            <div className="space-y-6">
              {questions && questions.length > 0 ? questions.map((question, index) => {
                // 确保question对象结构正确
                if (!question || !question.questionId || !question.questionContent || !Array.isArray(question.options) || !question.correctAnswer) {
                  return null;
                }

                const userAnswer = safeAnswers[question.questionId] || 'Not answered';
                const isCorrect = userAnswer === question.correctAnswer;

                return (
                  <div key={question.questionId} className="border rounded-lg p-4 hover:shadow-sm transition-shadow">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="text-sm font-medium text-gray-700">Question {index + 1}</h4>
                      <span
                        className={`text-sm font-medium px-2 py-1 rounded-full ${isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}
                      >
                        {isCorrect ? 'Correct' : 'Incorrect'}
                      </span>
                    </div>
                    <p className="text-gray-900 mb-3">{question.questionContent}</p>
                    <div className="space-y-2 mb-3">
                      {question.options.map((option, optIndex) => (
                        <div key={optIndex} className="flex items-center">
                          <input
                            type="radio"
                            name={`question-${question.questionId}`}
                            checked={option === userAnswer}
                            disabled
                            className={`mr-2 ${option === question.correctAnswer ? 'text-green-500 border-green-500' : option === userAnswer ? 'text-red-500 border-red-500' : 'text-gray-400'}`}
                          />
                          <label
                            className={`text-sm ${option === question.correctAnswer ? 'text-green-700 font-medium' : option === userAnswer ? 'text-red-700' : 'text-gray-600'}`}
                          >
                            {option}
                            {option === question.correctAnswer && (
                              <span className="ml-2 text-xs text-green-500">(Correct answer)</span>
                            )}
                          </label>
                        </div>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600">{question.explanation || 'No explanation'}</p>
                  </div>
                );
              }) : (
                <p className="text-gray-500 text-center py-8">No question explanations available</p>
              )}
            </div>
          </div>

          <div className="text-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="px-6 py-3 text-base font-medium text-white bg-primary rounded-md hover:bg-primary/90 transition-colors"
            >
              Back to Home
            </button>
            <button
              onClick={() => navigate('/test')}
              className="px-6 py-3 text-base font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
            >
              Retake Test
            </button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default TestResultPage;
